import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDown, Eye, EyeOff, Building, Building2, LayoutList, MapPin, Search } from "lucide-react";
import { viewAllCity } from "../../Redux/Actions/settingsActions";
import "./dropDown.css";

// cityValue / onCityChange are optional: city section renders only when onCityChange is passed
const BookingSourceDropdown = ({ value, onChange, cityValue, onCityChange }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedType, setExpandedType] = useState(null);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const wrapperRef = useRef(null);
  const hasFetchedCities = useRef(false);

  const showCity = typeof onCityChange === "function";
  const { citiesList } = useSelector((state) => state.city || {});
  const cityData = citiesList?.data ?? [];

  useEffect(() => {
    if (showCity && !cityData.length && !hasFetchedCities.current) {
      dispatch(viewAllCity());
      hasFetchedCities.current = true;
    }
  }, [dispatch, showCity, cityData.length]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const emit = (val) => onChange({ target: { value: val } });

  const handleSelectAll = () => {
    emit("");
    setExpandedType(null);
    setIsOpen(false);
  };

  const handleToggleType = (type) => {
    if (expandedType === type) {
      setExpandedType(null);
    } else {
      setExpandedType(type);
      if (!(Array.isArray(value) && expandedType === type)) {
        emit(type);
      }
    }
  };

  const handleSelectSub = (type, sub) => {
    setExpandedType(type);
    if (Array.isArray(value) && value[0] === sub && expandedType === type) {
      emit(type);
    } else {
      emit([sub]);
    }
    setIsOpen(false);
  };

  const handleSelectCity = (id) => {
    onCityChange({ target: { value: id === cityValue ? "" : id } });
    setCitySearch("");
    setIsOpen(false);
  };

  const handleClearCity = () => {
    onCityChange({ target: { value: "" } });
    setCitySearch("");
  };

  const filteredCities = cityData.filter((c) =>
    (c.city_name || "").toLowerCase().includes(citySearch.toLowerCase())
  );

  const selectedCity = cityData.find((c) => c._id === cityValue);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const typeName = Array.isArray(value) ? expandedType : value;
  const appliedFilters = [
    typeName && `Type: ${cap(typeName)}`,
    Array.isArray(value) && `Status: ${cap(value[0])}`,
    cityValue && `City: ${selectedCity?.city_name || "Selected"}`,
  ].filter(Boolean);

  const isTypeActive = (type) =>
    value === type || (Array.isArray(value) && expandedType === type);

  const isSubActive = (type, sub) =>
    Array.isArray(value) && value[0] === sub && expandedType === type;

  return (
    <div className="pf-dd-wrapper" ref={wrapperRef}>
      <button
        className="pf-dd-trigger"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        title={appliedFilters.join("\n")}
      >
        <span className="pf-dd-label">Filters</span>
        {appliedFilters.length > 0 && (
          <span className="pf-dd-count">{appliedFilters.length}</span>
        )}
        <ChevronDown
          size={16}
          className={`pf-dd-chevron ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="pf-dd-menu">

          <button
            className={`pf-dd-item ${!value || value === "" ? "active" : ""}`}
            onClick={handleSelectAll}
          >
            <LayoutList size={15} />
            <span className="pf-dd-item-label">All</span>
            {(!value || value === "") && <span className="pf-dd-check">✓</span>}
          </button>

          <div className="pf-dd-divider" />

          <button
            className={`pf-dd-item ${isTypeActive("residential") ? "active" : ""}`}
            onClick={() => handleToggleType("residential")}
          >
            <Building size={15} />
            <span className="pf-dd-item-label">Residential</span>
            {isTypeActive("residential") && <span className="pf-dd-check">✓</span>}
            <ChevronDown
              size={13}
              className={`pf-dd-sub-chevron ${expandedType === "residential" ? "open" : ""}`}
            />
          </button>

          {expandedType === "residential" && (
            <div className="pf-dd-sub-group">
              <button
                className={`pf-dd-sub-item ${isSubActive("residential", "published") ? "active" : ""}`}
                onClick={() => handleSelectSub("residential", "published")}
              >
                <Eye size={13} />
                <span>Published</span>
                {isSubActive("residential", "published") && <span className="pf-dd-check">✓</span>}
              </button>
              <button
                className={`pf-dd-sub-item ${isSubActive("residential", "unpublished") ? "active" : ""}`}
                onClick={() => handleSelectSub("residential", "unpublished")}
              >
                <EyeOff size={13} />
                <span>Unpublished</span>
                {isSubActive("residential", "unpublished") && <span className="pf-dd-check">✓</span>}
              </button>
            </div>
          )}

          <div className="pf-dd-divider" />

          <button
            className={`pf-dd-item ${isTypeActive("commercial") ? "active" : ""}`}
            onClick={() => handleToggleType("commercial")}
          >
            <Building2 size={15} />
            <span className="pf-dd-item-label">Commercial</span>
            {isTypeActive("commercial") && <span className="pf-dd-check">✓</span>}
            <ChevronDown
              size={13}
              className={`pf-dd-sub-chevron ${expandedType === "commercial" ? "open" : ""}`}
            />
          </button>

          {expandedType === "commercial" && (
            <div className="pf-dd-sub-group">
              <button
                className={`pf-dd-sub-item ${isSubActive("commercial", "published") ? "active" : ""}`}
                onClick={() => handleSelectSub("commercial", "published")}
              >
                <Eye size={13} />
                <span>Published</span>
                {isSubActive("commercial", "published") && <span className="pf-dd-check">✓</span>}
              </button>
              <button
                className={`pf-dd-sub-item ${isSubActive("commercial", "unpublished") ? "active" : ""}`}
                onClick={() => handleSelectSub("commercial", "unpublished")}
              >
                <EyeOff size={13} />
                <span>Unpublished</span>
                {isSubActive("commercial", "unpublished") && <span className="pf-dd-check">✓</span>}
              </button>
            </div>
          )}

          {showCity && (
            <>
              <div className="pf-dd-divider" />

              <button
                className={`pf-dd-item ${cityValue ? "active" : ""}`}
                onClick={() => setCityOpen((o) => !o)}
              >
                <MapPin size={15} />
                <span className="pf-dd-item-label">City</span>
                {cityValue && <span className="pf-dd-check">✓</span>}
                <ChevronDown
                  size={13}
                  className={`pf-dd-sub-chevron ${cityOpen ? "open" : ""}`}
                />
              </button>

              {cityOpen && (
                <div className="pf-dd-sub-group">
                  <div className="pf-dd-city-search">
                    <Search size={13} />
                    <input
                      type="text"
                      placeholder="Search cities..."
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                    />
                  </div>

                  <div className="pf-dd-city-list">
                    {filteredCities.length > 0 ? (
                      filteredCities.map((c) => (
                        <button
                          key={c._id}
                          className={`pf-dd-sub-item ${cityValue === c._id ? "active" : ""}`}
                          onClick={() => handleSelectCity(c._id)}
                        >
                          <MapPin size={13} />
                          <span>{c.city_name}</span>
                          {cityValue === c._id && <span className="pf-dd-check">✓</span>}
                        </button>
                      ))
                    ) : (
                      <div className="pf-dd-city-empty">No cities found</div>
                    )}
                  </div>

                  {cityValue && (
                    <button className="pf-dd-city-clear" onClick={handleClearCity}>
                      Clear city
                    </button>
                  )}
                </div>
              )}
            </>
          )}

        </div>
      )}
    </div>
  );
};

export default BookingSourceDropdown;